import type { ComponentDocs } from '../component-docs.js';

/**
 * What `<caos-card-grid>` says about itself.
 *
 * ── ONE LIST, FOUR BODIES ────────────────────────────────────────────────────
 * This is one of four pieces that draw the SAME record set four ways: `DataTable` as rows and
 * columns, this one as cards, `KanbanBoard` as stages, `SplitView` as a list beside a reader. The
 * example labelled "The orders list" is the same three records in all four, so the four can be
 * compared by looking rather than by reading four pages; `insteadUse` says where each of the other
 * three wins.
 *
 * ── WHY A CARD IS COMPACT ────────────────────────────────────────────────────
 * A card takes a NAME, a sub-line and up to two rollup metrics — not the record's full column set.
 * The source is explicit that this is the Accounts gold standard rather than the earlier Contacts
 * card that dumped every field; the "Accounts" example below is that card, kept from the samples
 * this file replaces.
 *
 * ── TWO THINGS WORTH KNOWING BEFORE YOU PLACE ONE ────────────────────────────
 * 1. `batchSize` is a NUMBER that can only be set as a property. The rule that turns an example's
 *    values into elements writes every primitive as an attribute, and this component observes no
 *    attributes at all — so a placement that sets `batchSize` would write `batch-size="12"` and be
 *    silently ignored. No example here sets it, and the input below carries no `attribute` to say
 *    so. It is the one shape the placement rule cannot express.
 * 2. An empty grid prints a plain sentence of its own rather than composing `EmptyState`, which is
 *    what `DataTable` does. That is a difference between two pieces that should agree, so it is
 *    shown as an example rather than left for somebody to discover in production.
 */
const docs: ComponentDocs = {
  aliases: ['cards', 'card list', 'tile grid', 'gallery view', 'card view', 'record cards'],

  answers: [
    'show a list of records as cards instead of table rows',
    'let somebody browse records on a narrow screen without a sideways scroll',
    'show a name and a couple of counts per record',
    'open a record by clicking its card',
  ],

  placements: ['recordPage', 'homePage', 'appPage'],

  inputs: [
    {
      name: 'items',
      type: 'json',
      description:
        'The cards. Each one an id, the record title, optionally the route it opens (href), a ' +
        'segment or type sub-line, and up to two labelled count/rollup metrics. A card may also ' +
        'carry its leading MARK: `icon`, a name from the platform glyph vocabulary standing for ' +
        'the record\'s kind, or `avatar` ({ src, name }), a picture of the record itself — a ' +
        'person\'s photograph, an organisation\'s logo. The avatar wins where both are given, and ' +
        'a card that says neither draws the generic record glyph, which is what every card drew ' +
        'before. Already access-filtered and formatted by the app layer — nothing is fetched ' +
        'here. Set as a property; an attribute cannot hold a list.',
    },
    {
      name: 'batchSize',
      type: 'number',
      description:
        'How many cards are painted per batch. The rest are appended as the end of the grid scrolls ' +
        'into view, so a long list does not paint all at once and nothing is dropped. Settable ONLY ' +
        'as a property (the component observes no attributes), and clamped to at least 1.',
      default: 24,
    },
  ],

  events: [
    {
      name: 'caos-card-open',
      when: 'A card is activated, by click or by keyboard — each card is a real button.',
      carries: '{ id } — the record the card stands for. The host routes it; the grid does not navigate.',
    },
  ],

  // NONE, read from the source: the grid builds every card itself and renders no <slot>, so there
  // is no way to project content into a card. A card's shape is what `items` describes and nothing
  // else.
  state: [],

  slots: [],

  tokens: [
    { token: '--caos-font', controls: 'The typeface of a card.' },
    { token: '--caos-color-surface', controls: 'The ground of a card at rest.' },
    { token: '--caos-color-border', controls: 'The hairline around a card at rest.' },
    { token: '--caos-radius-lg', controls: 'The corners of a card.' },
    { token: '--caos-shadow-card', controls: 'The elevation of a card.' },
    { token: '--caos-color-accent', controls: 'The record name, the icon tile, and the border a card takes on hover.' },
    { token: '--caos-accent-wash', controls: 'The tint a card fills with on hover.' },
    { token: '--caos-focus-ring', controls: 'The ring on a focused card.' },
    { token: '--caos-radius-sm', controls: 'The corners of the object icon tile.' },
    { token: '--caos-color-text-ghost', controls: 'The sub-line under the name, and the trailing caret.' },
    { token: '--caos-color-text-muted', controls: "A metric's label, and the sentence shown when the grid is empty." },
    { token: '--caos-color-text', controls: "A metric's figure." },
    { token: '--caos-space-4', controls: 'The gutter between cards, the padding inside a card, and the gap between metrics.' },
    { token: '--caos-space-3', controls: 'The gap between a card’s head and its metrics, and between the icon tile and the name.' },
    { token: '--caos-space-2', controls: 'The padding around the sentence shown when the grid is empty.' },
    { token: '--caos-space-1', controls: 'The gap between the name and its sub-line, and between a metric’s figure and its label.' },
  ],

  parts: [
    { part: 'grid', is: 'the grid the cards are laid out in' },
    { part: 'card', is: 'one card' },
  ],

  methods: [],

  placing:
    'A grid is the BODY of a list surface, under the header that names the list. It fills the width ' +
    'it is given and lays itself out in 320px tracks, so the number of columns follows the space ' +
    'rather than a setting. Each card brings its own border and elevation, so it wants a plain ' +
    'region around it rather than another panel.',

  whenToUse:
    'A list that is BROWSED rather than compared, where the record name is what somebody is looking ' +
    'for and one or two counts are enough context. It is also the list body that survives a narrow ' +
    'screen best, because cards reflow where a table has to scroll sideways.',

  insteadUse: [
    {
      name: 'DataTable',
      when:
        'The same field is being read across records and the values need lining up in columns — or ' +
        'rows need selecting, sorting or totalling, none of which a card can do.',
    },
    {
      name: 'KanbanBoard',
      when:
        'The question is where the work is: the same records stood up in columns by the field that ' +
        'says what stage each one is at.',
    },
    {
      name: 'SplitView',
      when:
        'Somebody reads records one after another and wants the one they picked open beside the ' +
        'list rather than on its own page.',
    },
    {
      name: 'StatTile',
      when: 'The card is ONE NUMBER rather than one record — a headline figure with its label and trend.',
    },
  ],

  accessibility:
    'Each card is a real button, so it is in the tab order, is activated by Enter and Space, and ' +
    'draws a focus ring — no roles or key handling to add. The icon tile and the caret are hidden ' +
    'from assistive technology, and a name too long for its card is truncated with the full text ' +
    "kept as its title. Left to the author: the card's accessible name is the title string, so a " +
    'title that reads "—" or a bare number tells a screen-reader user nothing about which record ' +
    'they are opening.',

  examples: [
    {
      key: 'orders',
      label: 'The orders list',
      summary:
        'The same three records the other three list bodies draw, as cards: the reference as the ' +
        'name, the account as the sub-line, and the value as a metric.',
      placement: {
        component: 'caos-card-grid',
        inputs: {
          items: [
            {
              id: 'o1',
              title: 'ORD-4471',
              subtitle: 'Meridian Supply · Draft',
              metrics: [{ value: '12,400', label: 'total' }],
            },
            {
              id: 'o2',
              title: 'ORD-4472',
              subtitle: 'Calder & Finch · Approved',
              metrics: [{ value: '3,150', label: 'total' }],
            },
            {
              id: 'o3',
              title: 'ORD-4473',
              subtitle: 'Brightwater Group · In review',
              metrics: [{ value: '86,900', label: 'total' }],
            },
          ],
        },
      },
    },
    {
      key: 'accounts',
      label: 'Accounts, with rollups',
      summary:
        'The compact card doing what it was measured against: a segment sub-line and two rollup ' +
        'counts — which is a different card from the orders above, where one figure was the point.',
      placement: {
        component: 'caos-card-grid',
        inputs: {
          items: [
            {
              id: 'a1',
              title: 'Meridian Supply',
              subtitle: 'Customer · Enterprise',
              metrics: [
                { value: '3', label: 'contacts' },
                { value: '2', label: 'orders' },
              ],
            },
            {
              id: 'a2',
              title: 'Calder & Finch',
              subtitle: 'Partner · Mid-market',
              metrics: [
                { value: '1', label: 'contact' },
                { value: '5', label: 'orders' },
              ],
            },
            {
              id: 'a3',
              title: 'Brightwater Group',
              subtitle: 'Prospect · Small business',
              metrics: [{ value: '1', label: 'order' }],
            },
          ],
        },
      },
    },
    {
      key: 'empty',
      label: 'Nothing to show',
      summary:
        'An empty grid, which prints one plain muted sentence of its own rather than composing an ' +
        'EmptyState the way DataTable does — so a surface that wants to say something useful has to ' +
        'say it above the grid.',
      placement: { component: 'caos-card-grid', inputs: { items: [] } },
    },
  ],
};

export default docs;
